import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Box, Button, CircularProgress, Fade, LinearProgress, Paper, Typography } from '@mui/material'
import { ChevronLeft } from 'lucide-react'

import { useAuth } from '../../context/SecurityContextProvider'
import { ensurePrimaryPage } from '../../services/api/PageDataService'
import {
    clearOnboardingPointer,
    getOnboardingPointer,
    setOnboardingPointer,
} from '../../utils/onboarding/onboarding'
import { onboardingStyles as s } from './onboardingStyles'

import UsernameStep from './steps/UsernameStep'
import AboutYouStep from './steps/AboutYouStep'
import ValuePropsStep from './steps/ValuePropsStep'
import VideoUploadStep from './steps/VideoUploadStep'
import SocialsStep from './steps/SocialsStep'
import LinksStep from './steps/LinksStep'
import FinalTouchesStep from './steps/FinalTouchesStep'
import LivePageStep from './steps/LivePageStep'
import PlanStep from './steps/PlanStep'

const STEPS = [
    UsernameStep,
    AboutYouStep,
    ValuePropsStep,
    VideoUploadStep,
    SocialsStep,
    LinksStep,
    FinalTouchesStep,
    LivePageStep,
    PlanStep,
]

export default function OnboardingPage() {
    const { userData, loading, isAuthenticated } = useAuth()

    const userId = userData?.userId

    const initial = useMemo(() => getOnboardingPointer(userId), [userId])
    const [step, setStep] = useState(initial?.step ?? 0)
    const [userType, setUserType] = useState(initial?.userType ?? null)
    const [page, setPage] = useState(null)
    const seeded = useRef(false)

    // userData loads asynchronously, so on a hard load of /onboarding the first render has no
    // userId and therefore no pointer — the useState initializers above see nothing. Seed the
    // starting position once the user is known: resume where they left off, or, when setup is
    // complete but no subscription is active, jump straight to the plan step (Vibio is paid-only,
    // so that is the only thing left to do).
    useEffect(() => {
        if (seeded.current || !userId) return
        seeded.current = true
        if (initial?.step != null) {
            setStep(initial.step)
            setUserType(initial.userType ?? null)
        } else if (userData?.didCompleteSetup) {
            setStep(STEPS.length - 1)
        }
    }, [userId, initial, userData])

    // The new /users/me doesn't carry pages (unlike the old API's userData.videoPages), so bootstrap
    // the caller's primary page here — creating it + a default theme if needed — to give the
    // socials/links steps a pageId. ensurePrimaryPage is single-flight, so StrictMode double-mount is safe.
    useEffect(() => {
        if (!isAuthenticated) return
        let active = true
        ensurePrimaryPage()
            .then((p) => { if (active) setPage(p) })
            .catch((err) => console.error('Failed to bootstrap primary page', err))
        return () => { active = false }
    }, [isAuthenticated])

    // Keep the localStorage pointer in sync so a user who bounces can resume. Waits for the seed
    // above, otherwise the commit where userId first arrives would write the not-yet-seeded step
    // back over the stored pointer.
    useEffect(() => {
        if (!userId || !seeded.current) return
        setOnboardingPointer(userId, { step, userType, startedAt: initial?.startedAt ?? Date.now() })
    }, [userId, step, userType, initial?.startedAt])

    // Scroll to top on each step change.
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [step])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        )
    }

    if (!isAuthenticated) return <Navigate to="/login" />

    // Already finished onboarding (plan chosen) — never show it again.
    if (userData?.hasActiveTier) {
        clearOnboardingPointer(userId)
        return <Navigate to="/studio" />
    }

    const isFirst = step === 0
    const isLast = step === STEPS.length - 1

    const goNext = () => setStep((prev) => Math.min(prev + 1, STEPS.length - 1))
    const goBack = () => setStep((prev) => Math.max(prev - 1, 0))

    // Called when the flow is completed (plan chosen). Clears the resume pointer.
    const finishOnboarding = () => clearOnboardingPointer(userId)

    const StepComponent = STEPS[step]
    const progress = ((step + 1) / STEPS.length) * 100
    // The plan step shows two pricing cards side by side and needs more room.
    const wide = step === STEPS.length - 1
    const widthOverride = wide ? '760px' : '520px'

    return (
        <Box sx={s.page}>
            <Box sx={{ ...s.progressWrap, maxWidth: widthOverride }}>
                <Box sx={s.progressTopRow}>
                    {!isFirst ? (
                        <Button onClick={goBack} startIcon={<ChevronLeft size={18} />} sx={s.backButton}>
                            Back
                        </Button>
                    ) : (
                        <Box />
                    )}
                    <Typography sx={s.stepCount}>
                        Step {step + 1} of {STEPS.length}
                    </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} sx={s.progressBar} />
            </Box>

            <Fade in key={step} timeout={250}>
                <Paper elevation={3} sx={{ ...s.card, maxWidth: widthOverride }}>
                    <StepComponent
                        userData={userData}
                        page={page}
                        pageId={page?.pageId}
                        userType={userType}
                        setUserType={setUserType}
                        goNext={goNext}
                        goBack={goBack}
                        isFirst={isFirst}
                        isLast={isLast}
                        finishOnboarding={finishOnboarding}
                    />
                </Paper>
            </Fade>
        </Box>
    )
}
